import React, { useMemo, useState, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled, { useTheme } from "styled-components";
import * as config from "config";

const normalizeSummaryQuestions = (questions = []) =>
  (Array.isArray(questions) ? questions : []).map((question, index) => {
    const questionId = question.id || question.question_id || String(index + 1);
    const criteriaList = Array.isArray(question.criteria) ? question.criteria : [];

    return {
      id: questionId,
      text:
        question.text ||
        question.title ||
        question.question ||
        question.description ||
        `Question ${index + 1}`,
      criteria: criteriaList.map((criteria, idx) => ({
        id: criteria.id || `${questionId}-${idx + 1}`,
        heading: criteria.heading || "",
        title: criteria.title || criteria.name || "",
        description: criteria.description || "",
        rating: criteria.rating || "",
        marks: criteria.marks ?? criteria.score ?? criteria.points ?? 0,
      })),
    };
  });

const Page = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.bodyBg || "#f5f6fa"};
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const BackLink = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.primary};
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 0;
`;

const SummaryHeader = styled.div`
  background: ${({ theme }) => theme.color2};
  color: ${({ theme }) => theme.button?.primary?.text || "#ffffff"};
  border-radius: 16px;
  padding: 24px 32px;
  display: grid;
  gap: 24px;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.12);
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MetaLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  opacity: 0.8;
`;

const MetaValue = styled.span`
  font-size: 16px;
  font-weight: 600;
`;

const QuestionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const EmptyState = styled.p`
  margin: 0;
  padding: 24px;
  border-radius: 12px;
  border: 1px dashed ${({ theme }) => theme.color5 || "#CBD5F5"};
  color: ${({ theme }) => theme.color2 || "#001B4A"};
  text-align: center;
  font-weight: 600;
`;

const QuestionCard = styled.div`
  background: ${({ theme }) => theme.color7 || "#ffffff"};
  border: 1px solid ${({ theme }) => theme.color5 || "#E2E8F0"};
  border-radius: 16px;
  padding: 18px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.primary};
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
  }
`;

const QuestionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const QuestionText = styled.span`
  font-size: 15px;
  font-weight: 500;
  color: ${({ theme }) => theme.color1 || "#272D36"};
  flex: 1 1 auto;
`;

const QuestionActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ToggleIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.primary};
  font-size: 18px;
  font-weight: 600;
  transition: background 0.2s ease, color 0.2s ease;
  background: ${({ $expanded, theme }) => ($expanded ? theme.primary : "transparent")};
  color: ${({ $expanded, theme }) =>
    $expanded ? theme.button?.primary?.text || "#ffffff" : theme.primary};
`;

const Marks = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: ${({ theme }) => theme.color2 || "#001B4A"};
  font-weight: 600;
`;

const MarkInput = styled.input`
  width: 56px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.color5 || "#E2E8F0"};
  background: ${({ theme }) => theme.color7 || "#ffffff"};
  text-align: center;
  font-weight: 600;
  color: ${({ theme }) => theme.color1 || "#272D36"};
`;

const CriteriaWrapper = styled.div`
  border: 1px solid ${({ theme }) => theme.primary};
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: ${({ theme }) => theme.color7 || "#ffffff"};
`;

const CriteriaItem = styled.div`
  border: 1px solid ${({ theme }) => `${theme.primary}33`};
  border-radius: 14px;
  padding: 16px 20px;
  display: grid;
  grid-template-columns: minmax(140px, 180px) 1fr auto;
  align-items: center;
  gap: 16px;
  background: ${({ theme }) => `${theme.primary}08`};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    align-items: flex-start;
  }
`;

const CriteriaLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-weight: 600;
  color: ${({ theme }) => theme.color2 || "#001B4A"};
  text-transform: capitalize;
`;

const CriteriaHeading = styled.span`
  font-size: 13px;
  opacity: 0.8;
`;

const CriteriaTitle = styled.span`
  font-size: 15px;
`;

const CriteriaDescription = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: ${({ theme }) => theme.color1 || "#272D36"};
`;

const CriteriaScore = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-end;
  font-weight: 600;
  color: ${({ theme }) => theme.color2 || "#001B4A"};

  @media (max-width: 768px) {
    align-items: flex-start;
  }
`;

const RatingBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 16px;
  border-radius: 999px;
  background: ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.button?.primary?.text || "#ffffff"};
  font-size: 12px;
  text-transform: uppercase;
`;

const MarksBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 16px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.primary};
  font-size: 12px;
`;

const StudentRubricSummary = () => {
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();
  const summary = location.state?.summary || {
    testName: "",
    subject: "",
    startDate: "",
    endDate: "",
    attempt: "",
    totalMarks: "",
    questions: [],
  };
  const backArrow = (theme?.direction || "ltr") === "rtl" ? "\u2192" : "\u2190";
  const [expanded, setExpanded] = useState(() => new Set());
  const questions = useMemo(
    () => normalizeSummaryQuestions(summary.questions),
    [summary.questions]
  );

  const toggleQuestion = useCallback((questionId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(questionId)) {
        next.delete(questionId);
      } else {
        next.add(questionId);
      }
      return next;
    });
  }, []);

  const handleKeyToggle = useCallback(
    (event, questionId) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        toggleQuestion(questionId);
      }
    },
    [toggleQuestion]
  );

  const stopPropagation = (event) => {
    event.stopPropagation();
  };

  return (
    <Page>
      <BackLink type="button" onClick={() => history.push(config.ROUTES.StudentRubrics)}>
        {backArrow} Back
      </BackLink>

      <SummaryHeader>
        <MetaItem>
          <MetaLabel>Test Name</MetaLabel>
          <MetaValue>{summary.testName}</MetaValue>
        </MetaItem>
        <MetaItem>
          <MetaLabel>Subject</MetaLabel>
          <MetaValue>{summary.subject}</MetaValue>
        </MetaItem>
        <MetaItem>
          <MetaLabel>Start Date</MetaLabel>
          <MetaValue>{summary.startDate}</MetaValue>
        </MetaItem>
        <MetaItem>
          <MetaLabel>End Date</MetaLabel>
          <MetaValue>{summary.endDate}</MetaValue>
        </MetaItem>
        <MetaItem>
          <MetaLabel>Attempt</MetaLabel>
          <MetaValue>{summary.attempt}</MetaValue>
        </MetaItem>
        <MetaItem>
          <MetaLabel>Total Marks</MetaLabel>
          <MetaValue>{summary.totalMarks}</MetaValue>
        </MetaItem>
      </SummaryHeader>

      <QuestionList>
        {questions.length === 0 ? (
          <EmptyState>No rubric questions to display.</EmptyState>
        ) : (
          questions.map((question, index) => {
            const isExpanded = expanded.has(question.id);
            const totalMarks = question.criteria?.reduce(
                 (sum, c) => sum + (Number(c.marks) || 0),
                    0
                  );
            return (
              <QuestionCard
                key={question.id}
                role="button"
                tabIndex={0}
                onClick={() => toggleQuestion(question.id)}
                onKeyDown={(event) => handleKeyToggle(event, question.id)}
                data-expanded={isExpanded}
              >
                <QuestionHeader>
                  <QuestionText>
                    {String(index + 1).padStart(2, "0")}. {question.text}
                  </QuestionText>
                  <QuestionActions>
                    <Marks onClick={stopPropagation}>
                      Marks <MarksBadge>{`${totalMarks} Marks`}</MarksBadge>
                      {/* <MarkInput
                        type="text"
                        maxLength={3}
                        onClick={stopPropagation}
                        onMouseDown={stopPropagation}
                        onTouchStart={stopPropagation}
                        onKeyDown={stopPropagation}
                        onFocus={stopPropagation}
                      /> */}
                    </Marks>
                    <ToggleIndicator $expanded={isExpanded}>
                      {isExpanded ? "−" : "+"}
                    </ToggleIndicator>
                  </QuestionActions>
                </QuestionHeader>

                {isExpanded && (
                  <CriteriaWrapper onClick={stopPropagation}>
                    {question.criteria.length === 0 ? (
                      <CriteriaDescription>No rubric criteria provided.</CriteriaDescription>
                    ) : (
                      question.criteria.map((criteria) => (
                        <CriteriaItem key={criteria.id}>
                          <CriteriaLabel>
                            <CriteriaHeading>{criteria.heading}</CriteriaHeading>
                            <CriteriaTitle>{criteria.title}</CriteriaTitle>
                          </CriteriaLabel>
                          <CriteriaDescription>{criteria.description}</CriteriaDescription>
                          <CriteriaScore>
                            {criteria.rating && <RatingBadge>{criteria.rating}</RatingBadge>}
                            <MarksBadge>{`${criteria.marks} Marks`}</MarksBadge>
                          </CriteriaScore>
                        </CriteriaItem>
                      ))
                    )}
                  </CriteriaWrapper>
                )}
              </QuestionCard>
            );
          })
        )}
      </QuestionList>
    </Page>
  );
};

export default StudentRubricSummary;


